// Filter rail — compact mode (chips). Shown when filters.length >= 3
// per the audit finding UX.2 (full editor below ~3 filters is fine,
// chips win once it gets crowded). The non-compact branch (full
// editor) stays inline in DataSurfaceV3 — it's a richer surface with
// many dispatchers and not worth the prop bloat to extract.
//
// Extracted from DataSurfaceV3 during the agent-velocity refactor.

import * as React from 'react';
import type { FilterRow } from '../v2/recipe-sql';
import { OP_SYMBOL } from './data-surface-utils';

export function FilterRailCompact({
  filters,
  uniqueByPath,
  setFiltersExpanded,
  removeFilter,
}: {
  filters: FilterRow[];
  uniqueByPath: string;
  setFiltersExpanded: (v: boolean) => void;
  removeFilter: (idx: number) => void;
}): React.ReactElement | null {
  if (filters.length === 0) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
      padding: '8px 10px', background: 'var(--bg-sunken)',
      border: '1px solid var(--line)', borderRadius: 6,
    }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {filters.length} filter{filters.length === 1 ? '' : 's'}
      </span>
      {filters.map((f, idx) => {
        const fieldStr = f.field_path.trim() || '(empty)';
        const opSymbol = OP_SYMBOL[f.op] ?? f.op;
        const showValue = !['is_present', 'is_empty'].includes(f.op);
        return (
          <span
            key={idx}
            onClick={() => setFiltersExpanded(true)}
            title="Click to expand the editor"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 4px 2px 8px',
              fontFamily: 'var(--mono)', fontSize: 11,
              background: 'var(--bg)', border: '1px solid var(--line)',
              borderRadius: 12, color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
          >
            <span style={{ color: 'var(--ink-3)' }}>{idx === 0 ? '' : '∧ '}</span>
            <code>{fieldStr}</code>
            <span style={{ color: 'var(--ink-3)' }}>{opSymbol}</span>
            {showValue && f.value && <code style={{ color: 'var(--accent)' }}>{f.value.slice(0, 24)}{f.value.length > 24 ? '…' : ''}</code>}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); removeFilter(idx); }}
              title="Remove filter"
              style={{
                marginLeft: 2, padding: '0 6px',
                background: 'transparent', border: 'none',
                color: 'var(--ink-3)', cursor: 'pointer',
                fontFamily: 'var(--mono)', fontSize: 13, lineHeight: 1,
              }}
            >×</button>
          </span>
        );
      })}
      {uniqueByPath.trim() && (
        <span
          onClick={() => setFiltersExpanded(true)}
          title="Click to expand the editor"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px',
            fontFamily: 'var(--mono)', fontSize: 11,
            background: 'var(--accent-soft)', border: '1px solid var(--accent-line)',
            borderRadius: 12, color: 'var(--accent)',
            cursor: 'pointer',
          }}
        >
          unique by <code>{uniqueByPath.trim()}</code>
        </span>
      )}
      <span style={{ flex: 1 }} />
      <button
        type="button"
        onClick={() => setFiltersExpanded(true)}
        className="btn sm ghost"
        style={{ padding: '2px 10px', fontSize: 11 }}
      >edit</button>
    </div>
  );
}
